import { useCallback } from 'react';
import { addToast } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';

import { breakdownPRD, type PRDBreakdownRequest } from '~/services/api';

import type { ProjectProps, ProjectWithTasks, TaskProps } from '~/types';
import { ProjectStatus } from '~/types';

import { useSSEStore } from '../stores/sseStore';
import { useUIStore } from '../stores/uiStore';
import { logger } from '../utils/logger';

import { productKeys } from './useProducts';
import { useProjects } from './useProjects';

export function useProjectOperations(onNavigateToProject?: (projectId: string | null) => void): {
  handleGenerateTasksFromPRD: (projectId: string, prdContent: string) => Promise<void>;
  handleProjectDelete: (projectId: string) => void;
  handleProjectUpdate: (projectId: string, updates: Partial<ProjectProps>) => void;
} {
  const { deleteProject, projects, updateProject } = useProjects();
  const queryClient = useQueryClient();
  const {
    setActiveProjectAssistantTab,
    setPendingPRDAutoStart,
    setProjectAssistantExpanded,
    startEditingProject,
    stopEditingProject,
  } = useUIStore();
  const { addActiveJob } = useSSEStore();

  const handleProjectUpdate = useCallback(
    (projectId: string, updates: Partial<ProjectProps>) => {
      logger.debug({ component: 'useProjectOperations', projectId }, 'handleProjectUpdate called', {
        updates,
      });

      // `project` is looked up on a best-effort basis from the global ['projects']
      // cache, which callers like ProductView never populate (they render from a
      // product-scoped query instead) — so it's frequently undefined there. Only
      // used below for the optional hadPRDBefore check; the update itself must not
      // depend on this lookup succeeding, or it silently no-ops whenever the global
      // cache hasn't loaded yet.
      const project = projects.find(p => p.id === projectId);

      // For non-temp projects, handle editing state
      // IMPORTANT: When entering edit mode (isEditing: true), do it immediately
      // When exiting edit mode (isEditing: false), do it AFTER API success to avoid race condition
      if ('isEditing' in updates && updates.isEditing) {
        startEditingProject(projectId);
      }

      const { openPRDGenerator, prdContent, ...apiUpdates } = updates;

      logger.debug({ component: 'useProjectOperations', projectId }, 'API updates prepared', {
        apiUpdates,
        prdContent: prdContent ? `${prdContent.length} chars` : undefined,
        openPRDGenerator,
      });

      // Check if we need to make an API call or just handle editing state
      const hasApiUpdates = Object.keys(apiUpdates).length > 0 || prdContent !== undefined;
      const shouldExitEditMode = 'isEditing' in updates && !updates.isEditing;

      if (hasApiUpdates) {
        // Optimistic cache update is handled by updateProject.onMutate (useProjects).
        const updatePayload = {
          ...(apiUpdates.title !== undefined && {
            title: apiUpdates.title,
          }),
          ...(apiUpdates.description !== undefined && {
            description: apiUpdates.description,
          }),
          ...(apiUpdates.details !== undefined && {
            details: apiUpdates.details,
          }),
          ...(apiUpdates.status !== undefined && {
            status: apiUpdates.status as ProjectStatus,
          }),
          // Note: prdContent is managed via PRDDocument, not project updates
        };

        logger.debug(
          { component: 'useProjectOperations', projectId },
          'Sending API update request',
          { updates: updatePayload },
        );

        updateProject.mutate(
          { id: projectId, updates: updatePayload },
          {
            onSuccess: (updatedProject): void => {
              logger.info(
                {
                  component: 'useProjectOperations',
                  projectId: updatedProject.id,
                },
                'Project updated successfully',
                {
                  updatedProject,
                  totalTasks: updatedProject.totalTasks,
                  completedTasks: updatedProject.completedTasks,
                },
              );

              // Keep this project's product board lanes/counts in sync (FE-005) —
              // e.g. a status change (TODO -> DONE) must move the card between
              // ProductView's columns and update each column's count badge.
              // updatedProject.productId is never populated by the PATCH
              // response (see useProjects.ts's updateProject.onSuccess
              // safeMerge comment), so this uses the pre-mutation `project`
              // lookup above instead — best-effort, same caveat as the
              // hadPRDBefore check below (the global ['projects'] cache
              // ProductView's board never populates, per FE-003). When it
              // misses, useProjects.ts's own updateProject.onSuccess already
              // invalidates the broader productKeys.all as a fallback.
              if (project?.productId) {
                void queryClient.invalidateQueries({
                  queryKey: productKeys.projects(project.productId),
                });
              }

              // Exit edit mode AFTER API success to avoid race condition with useEffect
              if ('isEditing' in updates && !updates.isEditing) {
                stopEditingProject(projectId);
              }

              onNavigateToProject?.(updatedProject.id);

              // Handle openPRDGenerator flag for existing projects
              // This triggers when user clicks "Generate PRD with AI" on an existing project
              if (openPRDGenerator) {
                logger.info(
                  {
                    component: 'useProjectOperations',
                    projectId: updatedProject.id,
                  },
                  'Opening PRD generator for existing project with auto-start',
                );

                // Signal PRDGenerator to auto-start
                setPendingPRDAutoStart(updatedProject.id);

                // Open PRD generation panel
                setActiveProjectAssistantTab('chat');
                setProjectAssistantExpanded(true);
              } else if (project) {
                // Check if PRD was added to an existing project (didn't have it before).
                // Skipped entirely when `project` wasn't found (see lookup comment
                // above) — we have no prior-PRD state to compare against.
                const hadPRDBefore = project.hasPrd;
                const hasPRDNow = prdContent && prdContent.length >= 50;

                if (!hadPRDBefore && hasPRDNow && prdContent) {
                  // PRD was just added to an existing project - open PRD panel for review
                  logger.info(
                    {
                      component: 'useProjectOperations',
                      projectId: updatedProject.id,
                    },
                    'PRD added to existing project, opening PRD panel for review',
                    { prdContentLength: prdContent.length },
                  );

                  // Open PRD generation panel so user can review PRD and generate tasks manually
                  setActiveProjectAssistantTab('chat');
                  setProjectAssistantExpanded(true);
                }
              }
            },
            onError: (error): void => {
              // Optimistic rollback is handled by updateProject.onError (useProjects).
              logger.error(
                { component: 'useProjectOperations', projectId },
                'Project update failed',
                error,
              );
            },
          },
        );
      } else if (shouldExitEditMode) {
        // No API updates, but user wants to exit edit mode (e.g., clicked cancel)
        stopEditingProject(projectId);
      }
    },
    [
      projects,
      queryClient,
      updateProject,
      startEditingProject,
      stopEditingProject,
      onNavigateToProject,
      setActiveProjectAssistantTab,
      setPendingPRDAutoStart,
      setProjectAssistantExpanded,
    ],
  );

  const handleProjectDelete = useCallback(
    (projectId: string) => {
      const previousProjects = queryClient.getQueryData<ProjectWithTasks[]>(['projects']);
      // Best-effort lookup for product-scoped cache invalidation (FE-005),
      // captured before deletion since the project won't be findable
      // anywhere afterward. See handleProjectUpdate's identical comment for
      // why this can miss (and why that's an acceptable best-effort gap).
      const productId = previousProjects?.find(p => p.id === projectId)?.productId;

      queryClient.setQueryData<ProjectWithTasks[]>(['projects'], (old = []) =>
        old.filter(p => p.id !== projectId),
      );
      queryClient.setQueryData<TaskProps[]>(['tasks', projectId], []);

      deleteProject.mutate(projectId, {
        onError: (): void => {
          queryClient.setQueryData(['projects'], previousProjects);
        },
        onSuccess: (): void => {
          void queryClient.invalidateQueries({ queryKey: ['projects'] });
          if (productId) {
            void queryClient.invalidateQueries({ queryKey: productKeys.projects(productId) });
          }
          onNavigateToProject?.(null);
        },
      });
    },
    [deleteProject, queryClient, onNavigateToProject],
  );

  const handleGenerateTasksFromPRD = useCallback(
    async (projectId: string, prdContent: string): Promise<void> => {
      if (!prdContent || prdContent.length < 50) {
        logger.warn(
          { component: 'useProjectOperations', projectId },
          'PRD content too short for task generation',
          { prdContentLength: prdContent.length },
        );
        addToast({
          color: 'warning',
          description: 'PRD content is too short. Please add more details to your PRD first.',
        });
        return;
      }

      logger.info(
        { component: 'useProjectOperations', projectId },
        'Initiating task generation from PRD',
      );

      try {
        const prdRequest: PRDBreakdownRequest = {
          project_id: projectId,
          stream_events: true,
          // Note: prd_content not sent - backend fetches from PRDDocument
        };

        const result = await breakdownPRD(prdRequest);

        logger.info(
          {
            component: 'useProjectOperations',
            projectId,
            jobId: result.job_id,
          },
          'PRD breakdown initiated successfully',
          {
            job_id: result.job_id,
            status: result.status,
            estimated_duration: result.estimated_duration,
          },
        );

        addActiveJob(result.job_id, projectId, result.estimated_duration);

        // Immediately invalidate breakdown status caches to trigger UI updates
        // This ensures the button updates instantly and polling starts
        await queryClient.invalidateQueries({ queryKey: ['prd-breakdown-status-batch'] });
        await queryClient.invalidateQueries({ queryKey: ['prd-breakdown-status', projectId] });

        // Keep this project's product board lanes/counts in sync (FE-005) —
        // e.g. a card's "PRD generating" state on ProductView's board. Unlike
        // handleProjectUpdate/handleProjectDelete, nothing else in this
        // hook's mutations already covers this path with a broader
        // fallback (useProjects.ts's productKeys.all invalidation lives on
        // updateProject/deleteProject specifically, not on PRD breakdown),
        // so this is the only invalidation this path gets. Best-effort
        // lookup, same caveat as handleProjectUpdate/handleProjectDelete's
        // identical comments — an imperative read here since, unlike
        // handleProjectUpdate, this callback doesn't already have a reactive
        // `projects` lookup in scope.
        const productId = queryClient
          .getQueryData<ProjectWithTasks[]>(['projects'])
          ?.find(p => p.id === projectId)?.productId;
        if (productId) {
          void queryClient.invalidateQueries({ queryKey: productKeys.projects(productId) });
        }

        logger.debug(
          { component: 'useProjectOperations', projectId },
          'Invalidated breakdown status caches to trigger UI refresh',
        );

        // Note: We don't automatically switch to activity tab here
        // Users can manually switch to the Activity tab when they're ready to see progress
      } catch (error) {
        // Handle specific error types
        const errorObj = error as {
          data?: { detail?: { error?: string; job_id?: string; message?: string } | string };
          status?: number;
        };

        // 409 Conflict - duplicate breakdown
        if (errorObj.status === 409) {
          const detail = errorObj.data?.detail;
          const errorCode = typeof detail === 'object' ? detail.error : null;
          const message = typeof detail === 'object' ? detail.message : detail;

          if (errorCode === 'ACTIVE_BREAKDOWN_EXISTS') {
            logger.warn(
              { component: 'useProjectOperations', projectId },
              'Duplicate breakdown attempt blocked by API',
              { existingJobId: typeof detail === 'object' ? detail.job_id : null },
            );
            addToast({
              color: 'warning',
              description: 'A task breakdown is already in progress for this project.',
            });
            return; // Don't throw - handled gracefully
          }

          // Generic 409 - project frozen
          logger.warn(
            { component: 'useProjectOperations', projectId },
            'Project operation blocked - project locked',
            { message },
          );
          addToast({
            color: 'warning',
            description: message || 'Project is locked during task generation.',
          });
          return; // Don't throw - handled gracefully
        }

        // All other errors - toast handled by global mutation cache
        logger.error(
          { component: 'useProjectOperations', projectId, prdContentLength: prdContent.length },
          'PRD breakdown failed',
          error instanceof Error ? error : new Error(String(error)),
        );

        throw error;
      }
    },
    [addActiveJob, queryClient],
  );

  return {
    handleGenerateTasksFromPRD,
    handleProjectUpdate,
    handleProjectDelete,
  };
}
